import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { Loader2, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useSeo } from "@/hooks/useSeo";
import { useHref, useT } from "@/i18n";
import { useCategoryLabel } from "@/i18n/categories";

export default function ClaimByCard() {
  const { listingId } = useParams();
  const href = useHref();
  const t = useT();
  const catLabel = useCategoryLabel();
  const [listing, setListing] = useState(null);
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [settings, setSettings] = useState(null);

  useSeo({ title: t("claimcard.seo.title") });

  useEffect(() => {
    api.get("/settings/public").then(({ data }) => setSettings(data)).catch(() => {});
  }, []);

  useEffect(() => {
    api.get(`/listings/${listingId}`).then(({ data }) => setListing(data)).catch(() => setListing(false));
  }, [listingId]);

  const submit = async (event) => {
    event.preventDefault();
    if (!email) return;
    setBusy(true);
    try {
      const { data } = await api.post(`/listings/${listing.id}/claim-checkout`, { email });
      window.location.href = data.payment_url;
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
      setBusy(false);
    }
  };

  if (listing === false) {
    return (
      <div className="mx-auto max-w-xl px-4 py-24 text-center sm:px-8">
        <h1 data-testid="claim-card-not-found" className="font-display text-2xl font-bold tracking-tight">
          {t("claimcard.notFound")}
        </h1>
        <Link to={href("/directory")} className="mt-8 inline-block">
          <Button data-testid="claim-card-back-button">{t("detail.backToDir")}</Button>
        </Link>
      </div>
    );
  }

  if (!listing) {
    return (
      <div className="grid h-80 place-items-center">
        <Loader2 className="h-6 w-6 animate-spin text-primary" />
      </div>
    );
  }

  const price = settings?.price_label || "RM5";

  return (
    <div className="mx-auto max-w-2xl px-4 py-14 sm:px-8">
      <span className="grid h-11 w-11 place-items-center rounded-md bg-slate-900 text-white">
        <ShieldCheck className="h-5 w-5" />
      </span>
      <h1 className="mt-6 font-display text-3xl font-extrabold tracking-tight sm:text-4xl">
        {t("claimcard.title", { name: listing.business_name })}
      </h1>
      <p className="mt-3 max-w-xl text-base leading-relaxed text-slate-600">
        {t("claimcard.intro", { price })}
      </p>

      <form onSubmit={submit} className="mt-10 rounded-lg border border-border bg-card p-6 shadow-sm">
        <div className="rounded-lg bg-slate-50 p-4">
          <p className="text-sm font-semibold text-slate-900">{listing.business_name}</p>
          <p className="text-sm text-slate-500">
            {catLabel(listing.category)} · {listing.district}, {listing.state}
          </p>
        </div>

        <Label htmlFor="claim-card-email" className="mt-8 block">
          {t("claimcard.email")}
        </Label>
        <Input
          id="claim-card-email"
          type="email"
          data-testid="claim-card-email-input"
          placeholder="nama@emel.com"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          className="mt-2 h-11"
        />
        <p className="mt-2 text-xs text-slate-500">{t("claimcard.emailNote")}</p>

        <div className="mt-6 flex items-center justify-between rounded-lg border border-border p-4">
          <span className="text-sm text-slate-600">{t("claimcard.priceLabel")}</span>
          <span data-testid="claim-price" className="font-display text-xl font-bold tracking-tight">
            {price}
          </span>
        </div>

        <Button
          type="submit"
          data-testid="claim-card-submit-button"
          disabled={busy || !email}
          className="mt-6 h-12 w-full bg-primary hover:bg-[#0052CC] sm:w-fit sm:px-8"
        >
          {busy && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
          {busy ? t("claimcard.preparing") : t("claimcard.cta", { price })}
        </Button>
        <p className="mt-3 text-xs text-slate-500">{t("claimcard.note")}</p>
      </form>
    </div>
  );
}
